"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, ChartLine as LineChart, ShieldCheck, SlidersHorizontal, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const metricHighlights = [{
  icon: Activity,
  label: "Inline Monitoring",
  value: "Real-Time PAT",
  description: "Continuous reaction tracking & blend uniformity"
}, {
  icon: ShieldCheck,
  label: "Compliance Ready",
  value: "21 CFR Part 11",
  description: "Fully validated data integrity protocols"
}, {
  icon: Cpu,
  label: "Spectroscopy Integration",
  value: "NIR & Raman",
  description: "Direct immersion and non-contact probes"
}];
const telemetryPoints = [{
  label: "Solvent Phase Recovery",
  value: "99.4%",
  status: "Optimal"
}, {
  label: "Blend Uniformity Index",
  value: "0.992 RSD",
  status: "In Spec"
}, {
  label: "Drying Endpoint Confidence",
  value: "99.8%",
  status: "Verified"
}];
export default function Hero() {
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background py-16 md:py-24 lg:py-28">
      {/* Decorative subtle background grid */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--color-secondary)_0%,_transparent_70%)] opacity-70" />
      
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          
          {/* Left Column: Value Proposition & CTAs */}
          <div className="flex flex-col items-start lg:col-span-7">
            <div className="mb-4 inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-secondary-foreground border border-border">
                <Zap className="h-3.5 w-3.5 text-primary" />
                <span>Process Guardian Platform</span>
              </Badge>
              <span className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
                Antech Solutions
              </span>
            </div>

            <h1 className="text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl lg:text-6xl font-default">
              Precision process monitoring for regulated manufacturing.
            </h1>

            <p className="mt-6 text-lg text-muted-foreground leading-relaxed md:text-xl">
              Application-led analytical technology, qualification, and lifecycle support. 
              Deploy inline NIR and Raman process analyzers to eliminate offline delays, 
              verify blend endpoints, and safeguard product quality across Ireland and the UK.
            </p>

            {/* CTAs */}
            <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/BookADemoPage">
                  <span>Book a Live Demo</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/ProcessAnalyticalTechnologyPage">
                  <span>Explore PAT Solutions</span>
                </Link>
              </Button>
            </div>

            {/* Key trust bullets */}
            <div className="mt-10 grid w-full grid-cols-1 gap-3 border-t border-border pt-6 sm:grid-cols-3">
              <div className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>Pharmaceutical Grade</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>UK & Ireland Support</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span>Turnkey Qualification</span>
              </div>
            </div>
          </div>

          {/* Right Column: Interactive Process Guardian Telemetry Card */}
          <div className="w-full lg:col-span-5">
            <Card className="border border-border shadow-lg bg-card text-card-foreground">
              <CardHeader className="border-b border-border pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground">
                      <SlidersHorizontal className="h-4 w-4" />
                    </div>
                    <div>
                      <CardTitle className="text-base font-semibold">Process Guardian Analytics</CardTitle>
                      <p className="text-xs text-muted-foreground">Active Process Stream: Reactor Bay #4</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs font-semibold">
                    Live Stream
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="space-y-6 pt-6">
                {/* Visual Telemetry Chart Simulation */}
                <div className="rounded-lg border border-border bg-secondary p-4">
                  <div className="flex items-center justify-between pb-2">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                      <LineChart className="h-4 w-4 text-primary" />
                      <span>Inline Spectral Absorption</span>
                    </div>
                    <span className="font-mono text-xs font-semibold text-primary">0.02s Refresh</span>
                  </div>
                  
                  {/* CSS-based responsive spectral wave visualization */}
                  <div className="flex h-20 items-end gap-1.5 pt-2">
                    {[40, 65, 45, 85, 95, 60, 48, 70, 88, 92, 54, 76, 62, 80, 50].map((height, idx) => <div key={idx} data-index={idx} className="flex-1 rounded-t bg-primary" style={{
                    height: `${height}%`,
                    opacity: 0.25 + idx % 4 * 0.2
                  }} />)}
                  </div>
                  <div className="mt-2 flex justify-between text-[10px] text-muted-foreground font-mono">
                    <span>900 nm</span>
                    <span>1400 nm (Water Peak)</span>
                    <span>1700 nm</span>
                  </div>
                </div>

                {/* Key Process Metrics */}
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Telemetry In-Process Verification
                  </p>
                  <div className="divide-y divide-border rounded-lg border border-border">
                    {telemetryPoints.map((item, i) => <div key={item.label} data-index={i} className="flex items-center justify-between p-3 text-sm">
                        <div>
                          <p className="font-medium text-foreground">{item.label}</p>
                          <p className="text-xs font-mono text-muted-foreground">{item.value}</p>
                        </div>
                        <Badge variant="secondary" className="text-[11px] font-semibold text-secondary-foreground">
                          {item.status}
                        </Badge>
                      </div>)}
                  </div>
                </div>

                {/* Action Footer link */}
                <div className="pt-2">
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to="/DiscussYourApplicationPage">
                      <span>Discuss Your Application With Engineers</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

        </div>

        {/* Bottom Feature Highlights */}
        <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
          {metricHighlights.map((feat, i) => {
          const IconComponent = feat.icon;
          return <div key={feat.label} data-index={i} className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm transition-[border-color,box-shadow] hover:border-border/80">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-secondary-foreground">
                  <IconComponent className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {feat.label}
                  </p>
                  <h3 className="mt-0.5 text-base font-bold text-foreground">
                    {feat.value}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {feat.description}
                  </p>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}